<template>
  <content-container title="线下完税" :table-row-count="tableData.length" style="height: calc(100% - 76px);">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-input v-model="params.code" placeholder="完税申请单号" clearable @keydown="onEnter" />
      </search-col>

      <search-col>
        <el-input v-model="params.invoiceSubName" placeholder="发票科目" clearable @keydown="onEnter" />
      </search-col>

      <search-col>
        <el-select
          v-model="params.status"
          placeholder="审核状态"
          clearable
        >
          <el-option
            :label="invoiceApplyStatusLabel[EInvoiceApplyStatus.InternalAudit]"
            :value="EInvoiceApplyStatus.InternalAudit"
          />
          <el-option
            :label="invoiceApplyStatusLabel[EInvoiceApplyStatus.Pass]"
            :value="EInvoiceApplyStatus.Pass"
          />
          <el-option
            :label="invoiceApplyStatusLabel[EInvoiceApplyStatus.Rejected]"
            :value="EInvoiceApplyStatus.Rejected"
          />
        </el-select>
      </search-col>

      <search-col>
        <el-input v-model="params.approveMessage" placeholder="审核备注" clearable @keydown="onEnter" />
      </search-col> 

      <search-col>
        <el-input v-model="params.serverName" placeholder="服务主体" clearable @keydown="onEnter" />
      </search-col>

      <search-col>
        <el-date-picker
          v-model="dateRange"
          style="width: 100%;"
          type="daterange"
          :disabled-date="disabledDate"
          start-placeholder="申请时间(起)"
          :default-time="defaultTimeFormat"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD HH:mm:ss"
          end-placeholder="申请时间(止)"
          @keydown.enter="doSearch"
        />
      </search-col>

      <search-col>
        <el-select
          v-model="params.offlineInvoiceStatus"
          placeholder="发票上传状态"
          clearable
        >
          <el-option
            v-for="(v, key) in uploadStatusList"
            :key="key"
            :label="v.label"
            :value="v.value"
          />
        </el-select>
      </search-col>

      <search-col>
        <el-select
          v-model="params.offlineTaxStatus"
          placeholder="完税凭证上传状态"
          clearable
        >
          <el-option
            v-for="(v, key) in uploadStatusList"
            :key="key"
            :label="v.label"
            :value="v.value"
          />
        </el-select>
      </search-col>

      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>

    <!-- 非搜索区域的额外按钮 -->
    <div class="mb-5">        
      <el-button 
        type="primary"
        :disabled="tableData?.length === 0"
        @click="exportTable"
      >
        <svg-icon icon="mingcute:file-export-line" />导出
      </el-button>
    </div>

    <!-- 表格数据 -->
    <el-table v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData">
      <el-table-column align="left" type="index" label="序号" :min-width="80" />
      <el-table-column align="left" prop="code" show-overflow-tooltip label="完税申请单号" :min-width="150" :formatter="(row) => isNull(row.code) ? '--' : row.code" />
      <el-table-column align="left" prop="invoiceSubName" show-overflow-tooltip label="完税发票科目" :min-width="180" />
      <el-table-column align="right" prop="invoiceAmount" label="发票金额" :min-width="150" />
      <el-table-column align="right" show-overflow-tooltip prop="taxAmount" label="税金（元）" :formatter="(row) => isNull(row.taxAmount) ? '--' : row.taxAmount" :min-width="120" />
      <el-table-column align="center" prop="status" label="审核状态" :min-width="120">
        <template #default="scope">
          <custom-tag
            :label="invoiceApplyStatusLabel[scope.row.status]"
            :type="invoiceApplyStatusStyle[scope.row.status]"
          />
        </template>
      </el-table-column>
      <el-table-column align="left" show-overflow-tooltip prop="approveMessage" label="审核备注" :formatter="(row) => isNull(row.approveMessage) ? '--' : row.approveMessage" :min-width="150" />
      <el-table-column align="left" show-overflow-tooltip prop="serverName" label="服务主体" :min-width="200" />
      <el-table-column align="left" show-overflow-tooltip prop="createTime" label="提交申请时间" :formatter="(row) => isNull(row.createTime) ? '--' : row.createTime" :min-width="180" />
      <el-table-column align="center" prop="status" label="发票上传状态" :min-width="120">
        <template #default="scope">
          <custom-tag
            :label="invoiceUploadStatusLabel[scope.row.offlineInvoiceStatus]"
            :type="invoiceUploadStatusStyle[scope.row.offlineInvoiceStatus]"
          />
        </template>
      </el-table-column>
      <el-table-column align="center" prop="offlineTaxStatus" label="完税凭证上传状态" :min-width="150">
        <template #default="scope">
          <custom-tag
            :label="invoiceUploadStatusLabel[scope.row.offlineTaxStatus]"
            :type="invoiceUploadStatusStyle[scope.row.offlineTaxStatus]"
          />
        </template>
      </el-table-column>
      <el-table-column
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
        :min-width="225"
      >
        <template #default="scope">
          <table-row-buttons :buttons="getButtons(scope.row)" :row="scope.row" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>

    <!-- 上传弹窗 -->
    <upload-invoice
      :visible="showUploadInvoice"
      :type="uploadType"
      :row="currentRow"
      @close="showUploadInvoice = false"
      @confirm="doSearch()"
    />
  </content-container>
</template>

<script setup lang="ts">
import UploadInvoice from '../dialogs/UploadInvoice.vue';
import { reactive, ref, PropType, computed, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { SvgIcon, CustomTag, TableRowButtons } from '@vue3/components';
import {
  ContentContainer,
  SearchContainer,
  SearchCol,
  queryInvoiceApplyPage,
  useTblPermissionButtons,
  downloadInvoiceApplyFile,
  exportInvoiceApplyFile,
  useThrottle,
  queryInvoiceApplyExport,
} from '@core/main';
import {
  EInvoiceApplyStatus,
  IHnInvoiceApplyItem,
  IInvoiceApplyPageParams,
  invoiceApplyStatusLabel,
  invoiceApplyStatusStyle,
  EHnInvoiceType,
  IServerOptionsItem,
  invoiceUploadStatusLabel,
  invoiceUploadStatusStyle,
} from '@core/main/types';
import { downloadFile, getFomattedTimeRange, isNull } from '@core/tools';
import { useRouter } from 'vue-router';

defineOptions({ name: 'InvoiceTaxPayment' }); // 众包灵工 - 发票完税

const router = useRouter();

const props = defineProps({
  serverList: {
    type: Array as PropType<IServerOptionsItem[]>,
    default: () => [],
  },
  isPermission: {
    type: Boolean,
    default: false,
  },
});

const defaultPageSize = 12;

const params = reactive<IInvoiceApplyPageParams>({
  page: 1,
  size: defaultPageSize,
  hnInvoiceType: EHnInvoiceType.OFFLINE,
});
const loading = ref(false);
const total = ref(0);
const tableData = ref([] as IHnInvoiceApplyItem[]);
const showUploadInvoice = ref(false);
const currentRow = ref<IHnInvoiceApplyItem>();
const dateRange = ref<string[]>([]);
const uploadType = ref();
const uploadStatusList = ref(Object.keys(invoiceUploadStatusLabel).map((key) => ({
  label: invoiceUploadStatusLabel[key],
  value: Number(key),
})));

const disabledDate = (current: Date) => current.getTime() > Date.now();

const defaultTimeFormat = computed(() => getFomattedTimeRange());

function getButtons(row: IHnInvoiceApplyItem) {
  
  return row.status === EInvoiceApplyStatus.Rejected || row.status === EInvoiceApplyStatus.InternalAudit 
    ? buttons.value.filter((v) => v.name !== '导出' && v.name !== '上传发票' && v.name !== '下载发票' && v.name !== '上传完税凭证' && v.name !== '下载完税凭证' && v.name !== '重新上传发票' && v.name !== '重新完税凭证') 
    : row.offlineInvoiceStatus && row.offlineTaxStatus 
      ? buttons.value.filter((v) => v.name !== '上传发票' && v.name !== '上传完税凭证')
      : row.offlineInvoiceStatus && !row.offlineTaxStatus 
        ? buttons.value.filter((v) => v.name !== '上传发票' && v.name !== '重新完税凭证' && v.name !== '下载完税凭证')
        : !row.offlineInvoiceStatus && row.offlineTaxStatus 
          ? buttons.value.filter((v) => v.name !== '上传完税凭证' && v.name !== '重新上传发票' && v.name !== '下载发票')
          : buttons.value.filter((v) => v.name !== '下载发票' && v.name !== '下载完税凭证' && v.name !== '重新上传发票' && v.name !== '重新完税凭证');
}

const { buttons } = useTblPermissionButtons([
  {
    name: '详情',
    code: '',
    action: hanldeDetail,
  },
  {
    name: '导出',
    code: '',
    action: (row) => hanldeExport(row),
  },
  {
    name: '上传发票',
    code: '',
    action: hanldeUploadInvoice,
  },
  {
    name: '重新上传发票',
    code: '',
    action: hanldeUploadInvoice,
  },
  {
    name: '下载发票',
    code: '',
    action: (row) => handleDownLoad(row, 0),
  },
  {
    name: '上传完税凭证',
    code: '',
    action: hanldeUploadProof,
  },
  {
    name: '重新完税凭证',
    code: '',
    action: hanldeUploadProof,
  },
  {
    name: '下载完税凭证',
    code: '',
    action: (row) => handleDownLoad(row, 1),
  },
]);

async function exportTable() {
  try {
    const parmas = {
      ...getQueryParams(),
      businessType: 3, 
    } as IInvoiceApplyPageParams;

    const { buffer, fileName } = await queryInvoiceApplyExport(parmas);

    downloadFile(buffer, fileName);
    ElMessage.success('导出成功');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

const hanldeExport = useThrottle(onChangeExport, 800);

async function onChangeExport(row) {
  try {
    await exportInvoiceApplyFile(row.id);

    ElMessage.success('导出成功，请稍后在右上角的“下载中心”中查看');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function handleDownLoad(row: IHnInvoiceApplyItem, type: 0 | 1) {
  try {
    const res = await downloadInvoiceApplyFile(row.id, type);

    window.open(res, '_blank');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

function hanldeUploadInvoice(row: IHnInvoiceApplyItem) {
  uploadType.value = 0;

  currentRow.value = row;
  showUploadInvoice.value = true;
}

function hanldeUploadProof(row: IHnInvoiceApplyItem) {
  uploadType.value = 1;
  currentRow.value = row;
  showUploadInvoice.value = true;
}

function hanldeDetail(row) {
  router.push({
    name: 'CrowdSourceOfflineDetail',
    query: { id: row.id },
  });
}

function getQueryParams() {
  return {
    ...params,
    createTimeStart: isNull(dateRange.value?.[0]) ? undefined : dateRange.value[0],
    createTimeEnd: isNull(dateRange.value?.[1]) ? undefined : dateRange.value[1],
  };
}

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryInvoiceApplyPage(getQueryParams());

    tableData.value = data;
    total.value = all;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function resetForm() {
  dateRange.value = [];

  for (const key in params) {
    if (!['page', 'size', 'hnInvoiceType'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

watch(
  () => props.isPermission,
  (val) => {
    if (!val) {
      fetchData();
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.export-box {
  @apply mb-[20px];

  :deep(.el-button) {
    @apply px-2;
  }
}

.download-icon {
  @apply flex items-center gap-x-1;
}
</style>
